import React, { PureComponent } from "react";
import _ from "lodash";
import autoBind from "react-autobind";
import { Button, ButtonGroup, Checkbox, ControlLabel, FormControl, FormGroup, Panel } from "react-bootstrap";
import { Controlled as CodeMirror } from "react-codemirror2";

import { codeMirrorOptionsJson } from "../../../constants/appConstants";
import { createTaxonomyTemplate, normalizeTaxonomyDocument, slugifyTaxonomyId } from "../../../utils/taxonomyUtils";

require("codemirror/lib/codemirror.css"); // eslint-disable-line
require("codemirror/theme/material.css"); // eslint-disable-line
require("codemirror/mode/javascript/javascript"); // eslint-disable-line
require("codemirror/addon/fold/foldcode"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter"); // eslint-disable-line
require("codemirror/addon/fold/brace-fold.js"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter.css"); // eslint-disable-line
require("codemirror/addon/lint/lint"); // eslint-disable-line
require("codemirror/addon/lint/json-lint"); // eslint-disable-line
require("codemirror/addon/lint/lint.css"); // eslint-disable-line

function stringifyTaxonomy(taxonomy) {
	return JSON.stringify(taxonomy, null, 4);
}

function parseInitialTaxonomy(content) {
	try {
		return normalizeTaxonomyDocument(content);
	} catch (err) {
		console.warn("Unable to parse taxonomy content, using a new vocabulary template.", err);
		return createTaxonomyTemplate();
	}
}

function normalizeListValue(value = "") {
	return `${value || ""}`.split(",").map((item) => item.trim()).filter(Boolean);
}

export default class TaxonomyEditor extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);

		const taxonomy = parseInitialTaxonomy(props.content);
		const rawContent = stringifyTaxonomy(taxonomy);
		this.lastValidContent = rawContent;
		this.state = {
			mode: "visual",
			taxonomy,
			rawContent,
			rawValid: true,
			rawError: null
		};

		this.emitChange(taxonomy);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.content !== this.props.content) {
			const taxonomy = parseInitialTaxonomy(this.props.content);
			const rawContent = stringifyTaxonomy(taxonomy);
			this.lastValidContent = rawContent;
			this.setState({
				taxonomy,
				rawContent,
				rawValid: true,
				rawError: null
			});
		}
	}

	getContent() {
		return this.state.rawValid ? this.state.rawContent : this.lastValidContent;
	}

	emitChange(taxonomy) {
		const rawContent = stringifyTaxonomy(taxonomy);
		this.lastValidContent = rawContent;
		if (_.isFunction(this.props.onChange)) {
			this.props.onChange(rawContent);
		}
		if (_.isFunction(this.props.onValidityChange)) {
			this.props.onValidityChange({ valid: true, error: null });
		}
	}

	updateTaxonomy(updater) {
		this.setState((prev) => {
			const taxonomy = _.cloneDeep(prev.taxonomy);
			updater(taxonomy);
			const rawContent = stringifyTaxonomy(taxonomy);
			this.lastValidContent = rawContent;
			if (_.isFunction(this.props.onChange)) {
				this.props.onChange(rawContent);
			}
			if (_.isFunction(this.props.onValidityChange)) {
				this.props.onValidityChange({ valid: true, error: null });
			}
			return {
				taxonomy,
				rawContent,
				rawValid: true,
				rawError: null
			};
		});
	}

	handleModeChange(mode) {
		this.setState({ mode });
	}

	handleDocumentFieldChange(field, value) {
		this.updateTaxonomy((taxonomy) => {
			taxonomy[field] = value;
		});
	}

	handleScopeChange(value) {
		this.updateTaxonomy((taxonomy) => {
			taxonomy.intendedScopes = normalizeListValue(value);
		});
	}

	handleDefaultSelectionChange(field, value) {
		this.updateTaxonomy((taxonomy) => {
			taxonomy.defaultSelection = {
				...(taxonomy.defaultSelection || {}),
				[field]: value
			};
		});
	}

	handleFacetFieldChange(facetIndex, field, value) {
		this.updateTaxonomy((taxonomy) => {
			const facet = taxonomy.facets[facetIndex];
			facet[field] = value;
			if (field === "label" && !facet.id) {
				facet.id = slugifyTaxonomyId(value, `facet-${facetIndex + 1}`);
			}
		});
	}

	handleCategoryFieldChange(facetIndex, categoryIndex, field, value) {
		this.updateTaxonomy((taxonomy) => {
			const category = taxonomy.facets[facetIndex].categories[categoryIndex];
			category[field] = value;
			if (field === "label" && !category.id) {
				category.id = slugifyTaxonomyId(value, `category-${categoryIndex + 1}`);
			}
		});
	}

	handleValueFieldChange(facetIndex, categoryIndex, valueIndex, field, value) {
		this.updateTaxonomy((taxonomy) => {
			const values = this.getEditableValues(taxonomy.facets[facetIndex], categoryIndex);
			const entry = values[valueIndex];
			if (field === "aliases") {
				entry.aliases = normalizeListValue(value);
			} else {
				entry[field] = value;
			}
			if (field === "label" && !entry.id) {
				entry.id = slugifyTaxonomyId(value, `value-${valueIndex + 1}`);
			}
		});
	}

	handleRawChange(rawContent) {
		try {
			const taxonomy = normalizeTaxonomyDocument(rawContent);
			const normalizedRawContent = stringifyTaxonomy(taxonomy);
			this.lastValidContent = normalizedRawContent;
			this.setState({
				rawContent,
				taxonomy,
				rawValid: true,
				rawError: null
			});
			if (_.isFunction(this.props.onChange)) {
				this.props.onChange(rawContent);
			}
			if (_.isFunction(this.props.onValidityChange)) {
				this.props.onValidityChange({ valid: true, error: null });
			}
		} catch (err) {
			this.setState({
				rawContent,
				rawValid: false,
				rawError: err
			});
			if (_.isFunction(this.props.onValidityChange)) {
				this.props.onValidityChange({ valid: false, error: err });
			}
		}
	}

	getEditableValues(facet, categoryIndex) {
		if (Number.isInteger(categoryIndex) && Array.isArray(facet.categories)) {
			const category = facet.categories[categoryIndex];
			if (!Array.isArray(category.values)) {
				category.values = [];
			}
			return category.values;
		}

		if (!Array.isArray(facet.values)) {
			facet.values = [];
		}
		return facet.values;
	}

	addFacet() {
		this.updateTaxonomy((taxonomy) => {
			const index = taxonomy.facets.length + 1;
			taxonomy.facets.push({
				id: `facet-${index}`,
				label: `Facet ${index}`,
				required: false,
				selection: "multi",
				values: []
			});
		});
	}

	removeFacet(facetIndex) {
		this.updateTaxonomy((taxonomy) => {
			taxonomy.facets.splice(facetIndex, 1);
		});
	}

	addCategory(facetIndex) {
		this.updateTaxonomy((taxonomy) => {
			const facet = taxonomy.facets[facetIndex];
			if (!Array.isArray(facet.categories)) {
				facet.categories = [];
			}
			const index = facet.categories.length + 1;
			facet.categories.push({
				id: `category-${index}`,
				label: `Category ${index}`,
				values: []
			});
		});
	}

	removeCategory(facetIndex, categoryIndex) {
		this.updateTaxonomy((taxonomy) => {
			taxonomy.facets[facetIndex].categories.splice(categoryIndex, 1);
			if (taxonomy.facets[facetIndex].categories.length === 0) {
				delete taxonomy.facets[facetIndex].categories;
			}
		});
	}

	addValue(facetIndex, categoryIndex = null) {
		this.updateTaxonomy((taxonomy) => {
			const facet = taxonomy.facets[facetIndex];
			const values = this.getEditableValues(facet, categoryIndex);
			const index = values.length + 1;
			values.push({
				id: `value-${index}`,
				label: `Value ${index}`
			});
		});
	}

	removeValue(facetIndex, categoryIndex, valueIndex) {
		this.updateTaxonomy((taxonomy) => {
			const values = this.getEditableValues(taxonomy.facets[facetIndex], categoryIndex);
			values.splice(valueIndex, 1);
		});
	}

	renderValueRows(facet, facetIndex, categoryIndex = null) {
		const values = this.getEditableValues(facet, categoryIndex);
		if (values.length === 0) {
			return (
				<div style={{ color: "#8b97a6", fontStyle: "italic", padding: "6px 0" }}>
					No values.
				</div>
			);
		}

		return values.map((value, valueIndex) => (
			<div key={`${facetIndex}-${categoryIndex || "direct"}-${valueIndex}`} style={{ display: "grid", gridTemplateColumns: "1fr 180px 1fr auto", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
				<FormControl
					type="text"
					value={value.label || ""}
					placeholder="Label"
					onChange={(event) => this.handleValueFieldChange(facetIndex, categoryIndex, valueIndex, "label", event.target.value)}
				/>
				<FormControl
					type="text"
					value={value.id || ""}
					placeholder="stable-id"
					onChange={(event) => this.handleValueFieldChange(facetIndex, categoryIndex, valueIndex, "id", event.target.value)}
				/>
				<FormControl
					type="text"
					value={(value.aliases || []).join(", ")}
					placeholder="Aliases"
					onChange={(event) => this.handleValueFieldChange(facetIndex, categoryIndex, valueIndex, "aliases", event.target.value)}
				/>
				<Button bsSize="small" onClick={() => this.removeValue(facetIndex, categoryIndex, valueIndex)}>
					Remove
				</Button>
			</div>
		));
	}

	renderCategory(category, facetIndex, categoryIndex) {
		const values = this.getEditableValues(this.state.taxonomy.facets[facetIndex], categoryIndex);
		return (
			<Panel key={`${facetIndex}-${categoryIndex}`} style={{ marginBottom: "10px" }}>
				<Panel.Heading>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
						<strong>{category.label || category.id || `Category ${categoryIndex + 1}`}</strong>
						<span style={{ color: "#6b778c", fontSize: "12px" }}>{values.length} tag value{values.length === 1 ? "" : "s"}</span>
					</div>
				</Panel.Heading>
				<Panel.Body>
					<div style={{ color: "#5f6f82", fontSize: "12px", marginBottom: "8px" }}>
						Category groups related tag values inside this facet.
					</div>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 200px auto", gap: "8px", alignItems: "center", marginBottom: "10px" }}>
						<div style={{ fontSize: "12px", fontWeight: 600, color: "#5f6f82" }}>Category Label</div>
						<div style={{ fontSize: "12px", fontWeight: 600, color: "#5f6f82" }}>Stable ID</div>
						<div></div>
						<FormControl
							type="text"
							value={category.label || ""}
							placeholder="Category label"
							onChange={(event) => this.handleCategoryFieldChange(facetIndex, categoryIndex, "label", event.target.value)}
						/>
						<FormControl
							type="text"
							value={category.id || ""}
							placeholder="category-id"
							onChange={(event) => this.handleCategoryFieldChange(facetIndex, categoryIndex, "id", event.target.value)}
						/>
						<Button bsSize="small" onClick={() => this.removeCategory(facetIndex, categoryIndex)}>
							Remove Category
						</Button>
					</div>
					<div style={{ fontSize: "12px", fontWeight: 600, color: "#5f6f82", marginBottom: "6px" }}>
						Tag Values
					</div>
					{this.renderValueRows(this.state.taxonomy.facets[facetIndex], facetIndex, categoryIndex)}
					<Button bsSize="small" onClick={() => this.addValue(facetIndex, categoryIndex)}>
						Add Value
					</Button>
				</Panel.Body>
			</Panel>
		);
	}

	renderFacet(facet, facetIndex) {
		const categories = Array.isArray(facet.categories) ? facet.categories : [];
		const directValues = Array.isArray(facet.values) ? facet.values : [];
		return (
			<Panel key={facetIndex} style={{ marginBottom: "14px" }}>
				<Panel.Heading>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
						<div>
							<strong>{facet.label || facet.id || `Facet ${facetIndex + 1}`}</strong>
							<div style={{ color: "#6b778c", fontSize: "12px", marginTop: "2px" }}>
								{categories.length > 0 ? `${categories.length} categor${categories.length === 1 ? "y" : "ies"}` : `${directValues.length} tag value${directValues.length === 1 ? "" : "s"}`}
							</div>
						</div>
						<Button bsSize="small" onClick={() => this.removeFacet(facetIndex)}>
							Remove Facet
						</Button>
					</div>
				</Panel.Heading>
				<Panel.Body>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 220px 140px 120px", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
						<FormGroup style={{ marginBottom: 0 }}>
							<ControlLabel>Label</ControlLabel>
							<FormControl
								type="text"
								value={facet.label || ""}
								onChange={(event) => this.handleFacetFieldChange(facetIndex, "label", event.target.value)}
							/>
						</FormGroup>
						<FormGroup style={{ marginBottom: 0 }}>
							<ControlLabel>ID</ControlLabel>
							<FormControl
								type="text"
								value={facet.id || ""}
								onChange={(event) => this.handleFacetFieldChange(facetIndex, "id", event.target.value)}
							/>
						</FormGroup>
						<FormGroup style={{ marginBottom: 0 }}>
							<ControlLabel>Selection</ControlLabel>
							<FormControl
								componentClass="select"
								value={facet.selection || "multi"}
								onChange={(event) => this.handleFacetFieldChange(facetIndex, "selection", event.target.value)}
							>
								<option value="multi">Multi</option>
								<option value="single">Single</option>
							</FormControl>
						</FormGroup>
						<FormGroup style={{ marginBottom: 0, paddingTop: "24px" }}>
							<Checkbox
								checked={!!facet.required}
								onChange={(event) => this.handleFacetFieldChange(facetIndex, "required", event.target.checked)}
							>
								Required
							</Checkbox>
						</FormGroup>
					</div>

					{categories.length > 0 ? (
						<div>
							{categories.map((category, categoryIndex) => this.renderCategory(category, facetIndex, categoryIndex))}
							<Button bsSize="small" onClick={() => this.addCategory(facetIndex)}>
								Add Category
							</Button>
						</div>
					) : (
						<div>
							{this.renderValueRows(facet, facetIndex)}
							<Button bsSize="small" onClick={() => this.addValue(facetIndex)}>
								Add Value
							</Button>
							<Button bsSize="small" style={{ marginLeft: "8px" }} onClick={() => this.addCategory(facetIndex)}>
								Add Category
							</Button>
						</div>
					)}
				</Panel.Body>
			</Panel>
		);
	}

	renderVisualEditor() {
		const { taxonomy } = this.state;
		return (
			<div style={{ padding: "10px" }}>
				<div style={{ background: "#f4f7fb", border: "1px solid #d8e2ef", borderRadius: "4px", color: "#34495e", lineHeight: 1.45, marginBottom: "12px", padding: "10px" }}>
					<strong>Structure:</strong> facets are the top-level groups in the vocabulary. Categories are optional subgroups inside a facet. Values are the selectable tags users can apply.
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: "10px", marginBottom: "12px" }}>
					<FormGroup>
						<ControlLabel>Name</ControlLabel>
						<FormControl
							type="text"
							value={taxonomy.name || ""}
							onChange={(event) => this.handleDocumentFieldChange("name", event.target.value)}
						/>
					</FormGroup>
					<FormGroup>
						<ControlLabel>ID</ControlLabel>
						<FormControl
							type="text"
							value={taxonomy.id || ""}
							onChange={(event) => this.handleDocumentFieldChange("id", event.target.value)}
						/>
					</FormGroup>
				</div>
				<FormGroup>
					<ControlLabel>Description</ControlLabel>
					<FormControl
						componentClass="textarea"
						rows={2}
						value={taxonomy.description || ""}
						onChange={(event) => this.handleDocumentFieldChange("description", event.target.value)}
					/>
				</FormGroup>
				<div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px", marginBottom: "12px" }}>
					<FormGroup>
						<ControlLabel>Scopes</ControlLabel>
						<FormControl
							type="text"
							value={(taxonomy.intendedScopes || []).join(", ")}
							onChange={(event) => this.handleScopeChange(event.target.value)}
						/>
					</FormGroup>
				</div>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
						<h4 style={{ margin: 0 }}>Facets</h4>
						<Button bsStyle="primary" onClick={this.addFacet}>
						Add Facet
					</Button>
				</div>
				{taxonomy.facets.map((facet, facetIndex) => this.renderFacet(facet, facetIndex))}
			</div>
		);
	}

	renderJsonEditor() {
		const { rawContent, rawError, rawValid } = this.state;
		return (
			<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
				{!rawValid && (
					<div style={{ color: "#a94442", background: "#f2dede", border: "1px solid #ebccd1", padding: "8px", margin: "0 0 8px 0" }}>
						{rawError ? rawError.message : "Invalid JSON"}
					</div>
				)}
				<CodeMirror
					value={rawContent}
					options={codeMirrorOptionsJson}
					onBeforeChange={(editor, data, value) => this.handleRawChange(value)}
					className="stretch"
				/>
			</div>
		);
	}

	render() {
		const { mode } = this.state;
		return (
			<div className="taxonomy-editor-shell" style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
				<div style={{ flex: 0, padding: "0 10px 10px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<ButtonGroup>
						<Button active={mode === "visual"} onClick={() => this.handleModeChange("visual")}>
							Visual
						</Button>
						<Button active={mode === "json"} onClick={() => this.handleModeChange("json")}>
							JSON
						</Button>
						</ButtonGroup>
				</div>
				<div className="taxonomy-editor-body" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingBottom: "16px" }}>
					{mode === "json" ? this.renderJsonEditor() : this.renderVisualEditor()}
				</div>
			</div>
		);
	}
}
